/**
 * v2.7.65+E126 · Driver Preferences Card
 * ═════════════════════════════════════════════════════════════════════
 * Three notification-channel toggles (SMS / Voice / Push) backed by a
 * localStorage cache so the flags render instantly on subsequent visits
 * with ZERO server calls until the driver actually flips a toggle.
 *
 *  Cache contract (per ops brief · "Preference Query Shielding"):
 *    · Load: read from localStorage → paint UI → single background
 *      revalidation against /api/driver/preferences.
 *    · Save: optimistic UI + PATCH → on failure, revert + toast.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Bell, Loader2, MessageSquare, Phone, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CACHE_KEY = "driver_notification_prefs_v1";
const DEFAULTS = {
  sms_notifications:    true,
  voice_dispatch_pings: true,
  push_notifications:   true,
};

const readCache = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      sms_notifications:    !!parsed.sms_notifications,
      voice_dispatch_pings: !!parsed.voice_dispatch_pings,
      push_notifications:   !!parsed.push_notifications,
    };
  } catch { return null; }
};
const writeCache = (prefs) => {
  try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(prefs)); }
  catch { /* quota */ }
};

const ROWS = [
  { key: "sms_notifications",    Icon: MessageSquare, label: "SMS Notifications",
    hint: "Booking, shift, and payout texts. Standard Twilio SMS." },
  { key: "voice_dispatch_pings", Icon: Phone,         label: "Voice Dispatch Pings",
    hint: "Automated phone calls for new bookings and urgent dispatches." },
  { key: "push_notifications",   Icon: Bell,          label: "Push Notifications",
    hint: "In-app / browser notifications on your device." },
];

export default function DriverPreferencesCard() {
  const [prefs, setPrefs] = useState(() => readCache() || DEFAULTS);
  const [loading, setLoading] = useState(!readCache());
  const [saving, setSaving] = useState(null);
  const hydratedOnce = useRef(false);

  // ── One-time background revalidation against the server (never
  //    blocks the initial paint — the cache already gave us a value).
  useEffect(() => {
    if (hydratedOnce.current) return;
    hydratedOnce.current = true;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/driver/preferences");
        if (cancelled) return;
        const fresh = {
          sms_notifications:    !!data.sms_notifications,
          voice_dispatch_pings: !!data.voice_dispatch_pings,
          push_notifications:   !!data.push_notifications,
        };
        setPrefs(fresh);
        writeCache(fresh);
      } catch { /* driver may be offline — cached values remain */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = useCallback(async (key) => {
    const before = prefs[key];
    const optimistic = { ...prefs, [key]: !before };
    setPrefs(optimistic);
    writeCache(optimistic);
    setSaving(key);
    try {
      const { data } = await api.patch("/driver/preferences", { [key]: !before });
      const server = {
        sms_notifications:    !!data.sms_notifications,
        voice_dispatch_pings: !!data.voice_dispatch_pings,
        push_notifications:   !!data.push_notifications,
      };
      setPrefs(server);
      writeCache(server);
    } catch (e) {
      // Roll back
      setPrefs(prefs);
      writeCache(prefs);
      toast.error(e?.response?.data?.detail || "Could not save preference — try again");
    } finally { setSaving(null); }
  }, [prefs]);

  return (
    <Card className="p-4 space-y-3" data-testid="driver-preferences-card">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-amber-500"/>
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900 dark:text-zinc-100">
          Notification Preferences
        </h3>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400 ml-auto"/>}
      </div>
      <p className="text-[11px] text-slate-500 dark:text-zinc-500">
        Cached on this device — server only pinged when you flip a toggle.
      </p>
      <div className="divide-y divide-slate-200 dark:divide-zinc-800">
        {ROWS.map(({ key, Icon, label, hint }) => (
          <div
            key={key}
            className="flex items-start justify-between gap-3 py-3"
            data-testid={`driver-pref-row-${key}`}
          >
            <div className="flex items-start gap-2 min-w-0">
              <Icon className="h-4 w-4 text-slate-500 dark:text-zinc-400 flex-shrink-0 mt-0.5"/>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 dark:text-zinc-100">{label}</div>
                <div className="text-[11px] text-slate-500 dark:text-zinc-500 leading-snug">{hint}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {saving === key && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400"/>}
              <Switch
                checked={!!prefs[key]}
                onCheckedChange={() => toggle(key)}
                disabled={saving === key}
                data-testid={`driver-pref-toggle-${key}`}
                aria-label={label}
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
